import type {
  HttpRequestContext,
  PresetResolver,
  TqlAstNode,
} from '../../utils/tql';
import { createTqlFilter, evaluateTql, parseTql } from '../../utils/tql';
import { BODY_TQL_FIELDS } from '../../utils/tql/body-field-mapping';
import { harRequestToTqlContext } from './harRequestToTqlContext';
import {
  mapHarEntryToHarRequest,
  type HARRequest,
} from './mapHarEntryToHarRequest';
import type { HarEntry } from '@terra/shared-schemas-tracker';

type SlimTqlMatch = 'match' | 'miss' | 'unknown';

const combineSlimTqlMatches = ({
  operator,
  left,
  right,
}: {
  operator: 'AND' | 'OR';
  left: SlimTqlMatch;
  right: SlimTqlMatch;
}): SlimTqlMatch => {
  if (operator === 'AND') {
    if (left === 'miss' || right === 'miss') {
      return 'miss';
    }
    return left === 'match' && right === 'match' ? 'match' : 'unknown';
  }

  if (left === 'match' || right === 'match') {
    return 'match';
  }
  return left === 'miss' && right === 'miss' ? 'miss' : 'unknown';
};

const evaluateSlimTql = ({
  node,
  context,
  resolvePreset,
  seenPresets = new Set(),
}: {
  node: TqlAstNode;
  context: HttpRequestContext;
  resolvePreset?: PresetResolver;
  seenPresets?: Set<string>;
}): SlimTqlMatch => {
  if (node.type === 'field' && BODY_TQL_FIELDS.has(node.field)) {
    return 'unknown';
  }

  if (node.type === 'preset') {
    if (!resolvePreset || seenPresets.has(node.presetName)) {
      return 'match';
    }
    const preset = resolvePreset(node.presetName);
    if (!preset) {
      return 'match';
    }
    const parsedPreset = parseTql(preset.query);
    if (!parsedPreset.success) {
      return 'match';
    }
    return evaluateSlimTql({
      node: parsedPreset.ast,
      context,
      resolvePreset,
      seenPresets: new Set([...seenPresets, node.presetName]),
    });
  }

  if (node.type === 'logical') {
    return combineSlimTqlMatches({
      operator: node.operator,
      left: evaluateSlimTql({
        node: node.left,
        context,
        resolvePreset,
        seenPresets,
      }),
      right: evaluateSlimTql({
        node: node.right,
        context,
        resolvePreset,
        seenPresets,
      }),
    });
  }

  if (node.type === 'not') {
    const result = evaluateSlimTql({
      node: node.expression,
      context,
      resolvePreset,
      seenPresets,
    });
    if (result === 'unknown') {
      return 'unknown';
    }
    return result === 'match' ? 'miss' : 'match';
  }

  if (node.type === 'group') {
    return evaluateSlimTql({
      node: node.expression,
      context,
      resolvePreset,
      seenPresets,
    });
  }

  return evaluateTql(node, context, resolvePreset) ? 'match' : 'miss';
};

export const createTrackedHarFetchErrorMatcher = ({
  tqlQuery,
  resolvePreset,
}: {
  tqlQuery: string;
  resolvePreset?: PresetResolver;
}): ((request: HARRequest) => boolean) => {
  const parsedQuery = parseTql(tqlQuery);
  if (!parsedQuery.success) {
    return () => true;
  }

  return (request: HARRequest): boolean =>
    evaluateSlimTql({
      node: parsedQuery.ast,
      context: harRequestToTqlContext(request),
      resolvePreset,
    }) !== 'miss';
};

export const createTrackedHarResponseBodyMatcher = ({
  tqlQuery,
  resolvePreset,
}: {
  tqlQuery: string;
  resolvePreset?: PresetResolver;
}): ((fullHar: HarEntry | null) => boolean) => {
  const tqlFilter = createTqlFilter(tqlQuery, resolvePreset);

  return (fullHar: HarEntry | null): boolean => {
    if (!fullHar) {
      return false;
    }
    return tqlFilter(harRequestToTqlContext(mapHarEntryToHarRequest(fullHar)));
  };
};
